import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Calculator, Settings, Building2, TrendingUp, AlertTriangle, CheckCircle2, Users, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import PlanningSettings from "./PlanningSettings";

/**
 * Production Volume Projections — Calculator + Settings.
 * Live recalculation: every input change debounced 300ms triggers /api/planning/calculate.
 */
export default function PlanningModule() {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("calculator");

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/planning/settings");
      setSettings(data);
    } catch (e) {
      toast.error("Failed to load planning settings");
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  if (!settings) return <div className="text-muted-foreground py-8" data-testid="planning-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="planning-module">
      <div>
        <div className="pnc-eyebrow mb-2">Module · Main Manager only</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#F5F5F5] tracking-tight" data-testid="planning-module-title">
          {settings.module_title}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Estimate required staff per role for a projected panel volume and see who's off during the period.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="planning-tabs">
          <TabsTrigger value="calculator" data-testid="planning-tab-calculator" className="gap-1.5">
            <Calculator className="w-4 h-4" strokeWidth={2}/> Calculator
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="planning-tab-settings" className="gap-1.5">
            <Settings className="w-4 h-4" strokeWidth={2}/> Settings
          </TabsTrigger>
        </TabsList>
        <TabsContent value="calculator" className="mt-5">
          <PlanningCalculator settings={settings}/>
        </TabsContent>
        <TabsContent value="settings" className="mt-5">
          <PlanningSettings settings={settings} onChanged={loadSettings}/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Calculator
// ─────────────────────────────────────────────────────────────────────────────
function PlanningCalculator({ settings }) {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedWs, setSelectedWs] = useState([]);            // [] = ALL workshops
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [isRange, setIsRange] = useState(false);
  const [projection, setProjection] = useState(0);
  const [includePending, setIncludePending] = useState(false);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const workshops = settings.workshops || [];

  // Debounced recalc whenever inputs change
  useEffect(() => {
    const t = setTimeout(async () => {
      setCalculating(true);
      try {
        const { data } = await api.post("/planning/calculate", {
          workshops: selectedWs,
          start,
          end: isRange ? end : start,
          panel_projection: Number(projection) || 0,
          include_pending: includePending,
        });
        setResult(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Calculation failed");
      } finally {
        setCalculating(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [selectedWs, start, end, isRange, projection, includePending]);

  const toggleWs = (id) => {
    setSelectedWs((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAllWs = () => setSelectedWs([]);
  const clearWs = () => setSelectedWs(workshops.map((w) => w.id));

  const totals = result?.totals || { required: 0, off: 0, available: 0 };
  const contractorsInScope = result?.contractors_in_scope || 0;

  return (
    <div className="space-y-6" data-testid="planning-calculator">
      {/* ── Inputs ─────────────────────────────────────────────────────── */}
      <div className="pnc-card p-5 sm:p-6 space-y-5">
        <div className="pnc-eyebrow">Inputs</div>

        {/* Workshops */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label className="pnc-eyebrow inline-flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5"/> Workshops
            </Label>
            <div className="text-[11px] text-muted-foreground flex items-center gap-2">
              <button type="button" onClick={selectAllWs} className="hover:text-[#F5F5F5] underline underline-offset-2" data-testid="ws-select-all">All</button>
              <span className="text-[#2A2A2A]">·</span>
              <button type="button" onClick={clearWs} className="hover:text-[#F5F5F5] underline underline-offset-2" data-testid="ws-clear">None (pick individually)</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {workshops.map((w) => {
              const active = selectedWs.length === 0 || selectedWs.includes(w.id);
              return (
                <button
                  key={w.id}
                  type="button"
                  data-testid={`ws-chip-${w.id}`}
                  onClick={() => toggleWs(w.id)}
                  className={`px-3 py-1.5 rounded-full text-[13px] border transition-colors ${
                    active
                      ? "bg-[#C0C0C0] text-[#0A0A0A] border-[#C0C0C0]"
                      : "bg-[#1E1E1E] text-[#A8A8A8] border-[#2A2A2A] hover:border-[#C0C0C0]/60"
                  }`}
                >
                  {w.name}
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {selectedWs.length === 0 ? "All workshops included" : `${selectedWs.length} selected`}
          </div>
        </div>

        {/* Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="pnc-eyebrow inline-flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5"/> {isRange ? "Start date" : "Date"}
              </Label>
              <label className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRange}
                  onChange={(e) => setIsRange(e.target.checked)}
                  className="accent-[#C0C0C0]"
                  data-testid="date-range-toggle"
                />
                Date range
              </label>
            </div>
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="bg-[#1E1E1E] border-[#2A2A2A]"
              data-testid="calc-start-date"
            />
          </div>
          {isRange && (
            <div className="space-y-1.5">
              <Label className="pnc-eyebrow">End date</Label>
              <Input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                min={start}
                className="bg-[#1E1E1E] border-[#2A2A2A]"
                data-testid="calc-end-date"
              />
            </div>
          )}
        </div>

        {/* Projection + toggle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div className="space-y-1.5">
            <Label className="pnc-eyebrow inline-flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5"/> Panel projection
            </Label>
            <Input
              type="number"
              min="0"
              value={projection}
              onChange={(e) => setProjection(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="e.g. 420"
              className="bg-[#1E1E1E] border-[#2A2A2A] font-mono text-2xl h-14"
              data-testid="calc-projection"
            />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#2A2A2A] bg-[#141414] px-4 py-3">
            <div>
              <div className="pnc-eyebrow">Absences counted</div>
              <div className="text-[13px] text-muted-foreground mt-0.5">
                {includePending ? "Approved + Pending (worst case)" : "Approved only"}
              </div>
            </div>
            <Switch checked={includePending} onCheckedChange={setIncludePending} data-testid="include-pending-toggle"/>
          </div>
        </div>
      </div>

      {/* ── KPI strip ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Contractors in scope" value={contractorsInScope} icon={<Users className="w-4 h-4"/>}/>
        <KpiCard label="Total staff required" value={totals.required}/>
        <KpiCard label="Off during period" value={totals.off} accent={totals.off > 0 ? "amber" : undefined}/>
        <KpiCard label="Total available" value={totals.available} accent={totals.available < totals.required ? "red" : "green"}/>
      </div>

      {/* ── Results table ──────────────────────────────────────────────── */}
      <div className="pnc-card overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <div>
            <div className="pnc-eyebrow">Staffing summary</div>
            <div className="text-[13px] text-muted-foreground mt-0.5">
              {calculating ? "Recalculating…" : "Updates live as you change any input."}
            </div>
          </div>
        </div>
        <Table data-testid="calc-results-table">
          <TableHeader>
            <TableRow>
              <TableHead>Production role</TableHead>
              <TableHead className="text-right">Panels/person</TableHead>
              <TableHead className="text-right">Required</TableHead>
              <TableHead className="text-right">In role</TableHead>
              <TableHead className="text-right">Off</TableHead>
              <TableHead className="text-right">Available</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(result?.rows || []).map((r) => (
              <TableRow key={r.role_id} data-testid={`calc-row-${r.role_name}`}>
                <TableCell className="font-medium">{r.role_name}</TableCell>
                <TableCell className="text-right font-mono">{r.panels_per_person}</TableCell>
                <TableCell className="text-right font-mono">{r.required}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{r.in_role_total}</TableCell>
                <TableCell className="text-right font-mono">{r.off}</TableCell>
                <TableCell className="text-right font-mono">{r.available}</TableCell>
                <TableCell>
                  {r.required === 0 ? (
                    <span className="text-muted-foreground text-[13px]">—</span>
                  ) : r.status === "ok" ? (
                    <span className="inline-flex items-center gap-1.5 text-[#34D399] bg-[#0F1F14] border border-[#1F5E3D] px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider">
                      <CheckCircle2 className="w-3 h-3"/> OK
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[#FCD34D] bg-[#1F1808] border border-[#8A6E1F] px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider">
                      <AlertTriangle className="w-3 h-3"/> Short by {r.shortage}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(result?.rows || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                  No production roles configured. Add roles in the Settings tab.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Who's off ──────────────────────────────────────────────────── */}
      {result && result.totals.off > 0 && (
        <div className="pnc-card p-5" data-testid="calc-whos-off">
          <div className="pnc-eyebrow mb-3">Off during period, by role</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {result.rows.filter((r) => r.off > 0).map((r) => (
              <div key={r.role_id} className="rounded-lg border border-[#2A2A2A] bg-[#141414] p-3">
                <div className="text-[13px] text-[#F5F5F5] font-medium mb-1.5">{r.role_name} · {r.off}</div>
                <ul className="text-[12px] text-muted-foreground space-y-0.5">
                  {r.off_contractors.map((c) => (
                    <li key={c.id}>{c.name} <span className="text-[#6B6B6B]">· {c.department}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon, accent }) {
  const accentClass =
    accent === "red"   ? "text-[#F87171]" :
    accent === "amber" ? "text-[#FCD34D]" :
    accent === "green" ? "text-[#34D399]" :
                         "text-[#F5F5F5]";
  return (
    <div className="pnc-card p-4">
      <div className="pnc-eyebrow flex items-center gap-1.5 text-[#A8A8A8]">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`font-heading text-3xl font-semibold tracking-tight mt-1 ${accentClass}`}>{value}</div>
    </div>
  );
}
